import { addLineItems, getCart, getCartError, updateCart, waitForSettle } from '@/cart/cart-store'
import { Cart, LineItemInput, UpdateCartInput } from '@/cart/types'
import { logInfo } from '@/utilities/log'
import { effect, signal } from '@preact/signals'

export type CartService = {
  getCart: typeof getCart
  getCartError: typeof getCartError
  addLineItems: typeof addLineItems
  updateCart: typeof updateCart
  waitForSettle: typeof waitForSettle
}

export type GiftWithPurchaseOptions = {
  variants?: number[] | null
  giftId?: number
  minQuantity?: number | null
  cart: CartService
}

type HandleGiftItemResult = {
  updates: UpdateCartInput['updates'] | null
  addItems: LineItemInput[] | null
}

export default class GiftWithPurchase {
  variants: number[]
  giftId: number
  minQuantity: number
  private _cart: CartService
  private _listening = false
  private updateTasks: AbortController[] = []
  private _isPending = signal(false)
  private _allowCheckout = signal(false)

  /**
   * A service to handle gift with purchase functionality.
   * Contains methods to determine if a gift can be added based on the cart contents,
   * and to update the cart accordingly. Intended to be used with GWP-related buy-stack components.
   *
   * @param options - Options for the GiftWithPurchase instance.
   * - `variants`: Array of variant IDs that qualify for the gift.
   * - `giftId`: The ID of the gift item.
   * - `minQuantity`: Minimum quantity required to qualify for the gift.
   * - `cart`: An object containing methods to interact with the cart.
   */
  constructor(options: GiftWithPurchaseOptions) {
    this.variants = options.variants ?? []
    this.giftId = options.giftId ?? NaN
    this.minQuantity = options.minQuantity ?? 1
    this._cart = options.cart
    const getAllowCheckout = () => {
      const cart = this._cart.getCart()
      if (!cart) {
        return false
      }
      if (this.isPending) {
        return false
      }
      const hasGift = !this.giftId || cart.items.some(({ id }) => id === this.giftId)
      return this.hasSufficientQuantity(cart) && hasGift
    }

    effect(() => {
      this._allowCheckout.value = getAllowCheckout()
    })
    this._allowCheckout.value = getAllowCheckout()
  }

  /**
   * Checks if the gift with purchase update is currently pending.
   * This is implemented with preact signals, so it can be subscribed to in components and effects.
   * @example
   * ```tsx
   * import {effect} from '@preact/signals';
   *
   * const gwp = new GiftWithPurchase({
   *   // options
   * });
   * effect(() => {
   *   if (gwp.isPending) {
   *     // Show loading state
   *   } else {
   *     // Show gift options
   *   }
   * });
   * ```
   */
  public get isPending() {
    return this._isPending.value
  }

  /**
   * Checks if the cart allows checkout with the gift.
   * This is implemented with preact signals, so it can be subscribed to in components and effects.
   * @example
   * ```tsx
   * import {effect} from '@preact/signals';
   * const gwp = new GiftWithPurchase({
   *   // options
   * });
   * effect(() => {
   *   if (gwp.allowCheckout) {
   *     // Enable checkout button
   *   } else {
   *     // Disable checkout button
   *   }
   * });
   * ```
   * @returns `true` if the cart allows checkout with the gift, otherwise `false`.
   */
  public get allowCheckout(): boolean {
    return this._allowCheckout.value
  }

  /**
   * Sets or updates the options for the GiftWithPurchase instance.
   * This method allows you to change the variants, gift ID, and minimum quantity required for the gift.
   * It will automatically trigger an update to the gift if any of the options have changed.
   *
   * @param options - Partial options to update the GiftWithPurchase instance.
   * - `variants`: Array of variant IDs that qualify for the gift.
   * - `giftId`: The ID of the gift item.
   * - `minQuantity`: Minimum quantity required to qualify for the gift.
   * @returns void
   */
  public setOptions(options: Partial<Omit<GiftWithPurchaseOptions, 'cart'>>) {
    const diff =
      (!!options.variants && String(this.variants) !== String(options.variants)) ||
      (!!options.giftId && this.giftId !== options.giftId) ||
      (!!options.minQuantity && this.minQuantity !== options.minQuantity)

    logInfo('Setting GWP options:', options)

    this.variants = options.variants ?? this.variants
    this.giftId = options.giftId ?? this.giftId
    this.minQuantity = options.minQuantity ?? this.minQuantity

    if (!diff) {
      return
    }

    void this.updateGift()
  }

  private cartSnapshot = ''

  /**
   * Listens to changes in the cart and updates the gift accordingly.
   * This method sets up an effect that will trigger whenever the cart changes.
   * If called multiple times, it will only set up the listener once.
   *
   * @returns void
   * @example
   * ```tsx
   * const gwp = new GiftWithPurchase({
   *   // options
   * });
   * gwp.listenToCart();
   * ```
   */
  public listenToCart() {
    if (this._listening) {
      return
    }

    effect(async () => {
      const cart = JSON.stringify(this._cart.getCart())
      const error = this._cart.getCartError()
      if (cart === this.cartSnapshot || error) {
        return
      }
      this.cartSnapshot = cart

      await this.updateGift()
    })

    this._listening = true
  }

  /**
   * Determines if the cart meets the criteria to allow checkout with the gift.
   * If no variants are specified, it checks if the total item count meets the minimum quantity.
   * If variants are specified, it checks if the total quantity of those variants meets the minimum quantity.
   *
   * @param cart - The current cart state.
   *
   * @returns `true` if the cart meets the criteria, otherwise `false`.
   */
  public hasSufficientQuantity(cart: Cart): boolean {
    const minQty = Math.max(this.minQuantity, 1)

    if (this.variants.length === 0) {
      return cart.item_count >= minQty
    }
    const totalQty = cart.items
      .filter(({ id }) => this.variants.includes(id))
      .reduce((sum, { quantity }) => sum + quantity, 0)

    return totalQty >= minQty
  }

  /**
   * Handles the logic for managing gift items in the cart.
   * It checks if the gift item should be added or updated based on the current cart state.
   * If the gift item is already in the cart, it ensures its quantity is set to 1.
   *
   * @param cart - The current cart state.
   * @returns An object containing:
   * - `updates`: Input object for updating the cart, or `null` if no updates needed.
   * - `addItems`: An array of items to add to the cart, or `null` if no items need to be added.
   *
   * @example
   * ```tsx
   * const result = giftWithPurchase.handleGiftItem(cart);
   * if (result.updates) {
   *   await cart.updateCart({updates: result.updates});
   * }
   * if (result.addItems) {
   *   await cart.addLineItems(result.addItems);
   * }
   * ```
   */
  public handleGiftItem(cart: Cart): HandleGiftItemResult {
    const updates: UpdateCartInput['updates'] = {}
    const allowGift = this.hasSufficientQuantity(cart)

    for (const item of cart.items) {
      if (item.id === -1) {
        return {
          updates: null,
          addItems: null
        }
      }

      if (item.id === this.giftId) {
        if (!allowGift) {
          updates[item.id] = 0
        }

        if (item.quantity !== 1) {
          updates[item.id] = 1
        }

        continue
      }

      if (!item.has_components) {
        continue
      }

      updates[item.id] = 0
    }

    const hasUpdate = Object.keys(updates).length > 0

    const isGiftInCart = cart.items.some(item => item.id === this.giftId)
    const giftItem = {
      id: this.giftId,
      quantity: 1
    }

    return {
      updates: hasUpdate ? updates : null,
      addItems: allowGift && !isGiftInCart ? [giftItem] : null
    }
  }

  /**
   * This method checks the current cart state, determines if a gift item should be added or updated,
   * and then performs the necessary updates to the cart.
   * If the gift item is already in the cart, it ensures its quantity is set to 1.
   * If the gift item is not in the cart and the conditions are met, it adds the gift item.
   *
   * @returns A promise that resolves when the cart has been updated with the gift item.
   */
  public async updateGift() {
    this._isPending.value = true
    logInfo('GWP updateGift called with giftId:', { giftId: this.giftId })
    for (const job of this.updateTasks) {
      job.abort()
    }
    this.updateTasks.length = 0

    if (!this.giftId || this.variants.length === 0) {
      this._isPending.value = false
      return
    }

    const job = new AbortController()
    this.updateTasks.push(job)

    // In case updateGift is called multiple times synchronously,
    // we want later calls to abort the previous ones.
    // This await ensures that the effect is settled before proceeding.
    await Promise.resolve()

    if (job.signal.aborted) {
      return
    }

    const cartState = this._cart.getCart()

    if (!cartState) {
      return
    }

    const { updates, addItems } = this.handleGiftItem(cartState)

    if (updates) {
      await this._cart.updateCart({ updates })
    }

    if (job.signal.aborted) {
      return
    }

    if (addItems) {
      await this._cart.addLineItems(addItems)
    }

    if (job.signal.aborted) {
      return
    }

    await this._cart.waitForSettle()

    if (job.signal.aborted) {
      return
    }

    this._isPending.value = false
    if (!addItems && !updates) {
      logInfo('No updates or items to add for GWP')
    }
  }
}
